/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import {
  ToggleButtonGroup,
  ToggleButton,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Box,
} from '@mui/material';
import React from 'react';
import type { ServiceInterface } from '@opengeoweb/shared';
import type { layerTypes } from '@opengeoweb/store';
import { ArrowDropDownDown } from '@opengeoweb/theme';
import { useLayerSelectTranslation } from '../../utils/i18n';

interface AddLayerParams {
  serviceUrl: string;
  layerName: string;
  serviceType?: ServiceInterface;
}

export interface DeleteLayerParams {
  layerId: string;
  layerIndex: number;
}

interface LayerAddRemoveButtonProps {
  layer: layerTypes.ReduxLayer;
  serviceUrl: string;
  serviceType?: ServiceInterface;
  allLayers: layerTypes.ReduxLayer[];
  allMapIds: string[];
  addLayer: ({ serviceUrl, layerName }: AddLayerParams) => void;
  addLayerToAllMaps: ({ serviceUrl, layerName }: AddLayerParams) => void;
  deleteLayer: ({ layerId, layerIndex }: DeleteLayerParams) => void;
  removeLayerFromAllMaps: (layerId: string) => void;
  mapLayers: layerTypes.ReduxLayer[];
}

export const LayerAddRemoveButton: React.FC<LayerAddRemoveButtonProps> = ({
  layer,
  serviceUrl,
  serviceType,
  allLayers,
  allMapIds,
  addLayer,
  addLayerToAllMaps,
  deleteLayer,
  removeLayerFromAllMaps,
  mapLayers,
}: LayerAddRemoveButtonProps) => {
  const { t } = useLayerSelectTranslation();
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLDivElement>(null);

  const foundLayerIndex = React.useMemo(
    () =>
      mapLayers.findIndex(
        (lr) => lr.name === layer.name && lr.service === serviceUrl,
      ),
    [mapLayers, layer.name, serviceUrl],
  );
  const foundLayer = foundLayerIndex !== -1 && mapLayers[foundLayerIndex];

  const layerExistsInWorkspace = React.useMemo(
    () =>
      allLayers.find(
        (current) =>
          current.name === layer.name && current.service === serviceUrl,
      ),
    [allLayers, layer.name, serviceUrl],
  );

  const existsInAllMaps = React.useMemo(
    () =>
      allMapIds.every((mapId) =>
        allLayers.some(
          (current) =>
            current.mapId === mapId &&
            current.name === layer.name &&
            current.service === serviceUrl,
        ),
      ),
    [allMapIds, allLayers, layer.name, serviceUrl],
  );

  const handleToggle = (): void => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event | React.SyntheticEvent): void => {
    if (anchorRef.current?.contains(event.target as HTMLElement)) {
      return;
    }
    setOpen(false);
  };

  const handleAddAll = (): void => {
    addLayerToAllMaps({ serviceUrl, layerName: layer.name!, serviceType });
    setOpen(false);
  };

  const handleRemoveAll = (id: string): void => {
    removeLayerFromAllMaps(id);
    setOpen(false);
  };

  return (
    <>
      <ToggleButtonGroup
        fullWidth={true}
        ref={anchorRef}
        style={{ height: '32px' }}
      >
        <ToggleButton
          className="tool"
          data-testid={`layerAddRemoveButton-${layer.name}`}
          fullWidth
          onClick={(): void => {
            !foundLayer
              ? addLayer({ serviceUrl, layerName: layer.name!, serviceType })
              : deleteLayer({
                  layerId: foundLayer.id!,
                  layerIndex: foundLayerIndex,
                });
          }}
          onMouseDown={(e): void => {
            e.stopPropagation();
          }}
          selected={!!foundLayer}
          style={{
            borderRadius: '3px 0 0 3px',
            height: 'inherit',
            minWidth: '62px',
            fontSize: '12px',
          }}
          value="selected"
        >
          {!foundLayer ? t('add') : t('remove')}
        </ToggleButton>
        <Box
          sx={{
            minWidth: '1px',
            backgroundColor:
              foundLayer || layerExistsInWorkspace
                ? 'geowebColors.buttons.primary.default.borderColor'
                : 'geowebColors.buttons.secondary.default.fill',
          }}
        />
        <ToggleButton
          className="tool"
          data-testid={`layerAddRemoveButton-dropdown-${layer.name}`}
          onClick={handleToggle}
          onMouseDown={(e): void => {
            e.stopPropagation();
          }}
          selected={!!layerExistsInWorkspace}
          style={{
            marginLeft: '0',
            minWidth: '32px',
            borderRadius: '0 3px 3px 0',
            height: 'inherit',
          }}
          value="dropdown"
        >
          <ArrowDropDownDown style={{ marginRight: '1px' }} />
        </ToggleButton>
      </ToggleButtonGroup>
      <Popper
        anchorEl={anchorRef.current}
        open={open}
        placement="bottom-end"
        role={undefined}
        style={{ zIndex: 1300 }}
        transition
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList autoFocusItem>
                  <MenuItem disabled={existsInAllMaps} onClick={handleAddAll}>
                    {t('add-to-all-maps')}
                  </MenuItem>
                  <MenuItem
                    disabled={!layerExistsInWorkspace}
                    onClick={() => {
                      layerExistsInWorkspace &&
                        handleRemoveAll(layerExistsInWorkspace.id!);
                    }}
                  >
                    {t('remove-from-all-maps')}
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};
